import { usePrivy } from '@privy-io/react-auth';
import { useEffect, useState, useMemo } from 'react';

function AuthComponent() {
  const { login, logout, authenticated, user, getAccessToken } = usePrivy();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [debugRedirectUrl, setDebugRedirectUrl] = useState<string | null>(null);

  // Get OAuth parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const response_type = urlParams.get('response_type');
  const client_id = urlParams.get('client_id');
  const redirect_uri = urlParams.get('redirect_uri');
  const state = urlParams.get('state');
  const code_challenge = urlParams.get('code_challenge');
  const scope = urlParams.get('scope');
  const resource = urlParams.get('resource');
  const debug_mode = urlParams.get('debug') || urlParams.get('debug_mode');

  // Detect client type based on redirect_uri and other indicators
  const isDebugClient = useMemo(() => {
    if (debug_mode === 'true' || debug_mode === '1') return true;
    if (!redirect_uri) return false;

    // Common patterns for debug/development tools
    const debugPatterns = [
      /localhost:\d+\/oauth\/callback\/debug/i,
      /127\.0\.0\.1:\d+\/oauth\/callback\/debug/i,
      /\/debug/i,
      /\/callback\/debug/i,
      /inspector/i,
      /debugger/i,
      /\/test\//i,
      /^http:\/\/localhost/i,
    ];

    return debugPatterns.some(pattern => pattern.test(redirect_uri));
  }, [redirect_uri, debug_mode]);

  // Validate required OAuth parameters
  useEffect(() => {
    // For OAuth 2.1 with PKCE, state is optional but redirect_uri and client_id are required
    if (!redirect_uri || !client_id) {
      setError('Missing required OAuth parameters (redirect_uri, client_id)');
    }
  }, [redirect_uri, client_id]);

  const handleAuth = async () => {
    if (!authenticated) {
      setLoading(true);
      try {
        await login();
      } catch (err) {
        console.error('Privy login failed:', err);
        setError('Authentication failed. Please try again.');
        setLoading(false);
      }
    }
  };

  // When user successfully authenticates, get Privy access token and redirect
  useEffect(() => {
    if (authenticated && user && redirect_uri) {
      const handleAuthSuccess = async () => {
        try {
          setLoading(true);

          // Get the Privy access token
          const privyAccessToken = await getAccessToken();

          if (!privyAccessToken) {
            throw new Error('Failed to obtain Privy access token');
          }

          console.log('Privy authentication successful for user:', user.id);
          console.log(
            'Privy access token obtained:',
            privyAccessToken.substring(0, 50) + '...'
          );

          // Redirect back to MCP server callback endpoint with Privy token
          // The MCP server will then handle the OAuth completion and redirect to Claude Desktop

          // Extract the MCP server URL from the current URL's state parameter
          // The state contains the original OAuth request info including where it came from
          let mcpServerUrl = '';
          try {
            if (state) {
              const stateData = JSON.parse(window.atob(state));
              // Extract the redirect_uri from the state, which points to the MCP server's callback
              if (stateData.redirectUri) {
                const redirectUri = new URL(stateData.redirectUri);
                mcpServerUrl = redirectUri.origin;
              }
            }
          } catch (e) {
            console.error('Failed to parse state parameter:', e);
          }

          // Fallback to constructing the URL if state parsing fails
          if (!mcpServerUrl) {
            // For Cloudflare deployment, convert pages URL to workers URL
            if (window.location.hostname.includes('.pages.dev')) {
              // Handle both naming patterns
              if (window.location.hostname.includes('ee-mcp-lib')) {
                // For ee-mcp-lib.pages.dev -> cloudflare-mcp-adapter.tim-7dc.workers.dev
                mcpServerUrl = `https://cloudflare-mcp-adapter.tim-7dc.workers.dev`;
              } else {
                mcpServerUrl = window.location.origin
                  .replace(
                    'execution-escrow-mcp-interface',
                    'cloudflare-mcp-adapter'
                  )
                  .replace('.pages.dev', '.workers.dev');
              }
            } else if (window.location.hostname === 'localhost') {
              // For local development
              mcpServerUrl = 'http://localhost:8788';
            } else {
              // For custom domains, assume the MCP server is at a similar domain
              mcpServerUrl = window.location.origin.replace(
                'mcp-interface',
                'mcp-adapter'
              );
            }
          }

          const redirectUrl = new URL('/callback', mcpServerUrl);
          redirectUrl.searchParams.set('code', privyAccessToken);

          // Only include state if it was provided
          if (state) {
            redirectUrl.searchParams.set('state', state);
            console.log('Including state in redirect:', state);
          }

          console.log('MCP Server URL:', mcpServerUrl);
          console.log('Callback Redirect URL:', redirectUrl.toString());

          if (isDebugClient) {
            console.log('Debug mode detected - showing manual copy option');
            // For debug clients, show the code but don't auto-redirect
            setDebugCode(privyAccessToken);
            setDebugRedirectUrl(redirectUrl.toString());
          } else {
            console.log(
              'Production mode - redirecting to MCP server callback:',
              redirectUrl.toString()
            );
            // For production clients, auto-redirect after a brief delay
            setTimeout(() => {
              window.location.href = redirectUrl.toString();
            }, 1500);
          }
        } catch (err) {
          console.error('Error getting Privy access token:', err);
          setError('Failed to obtain access token. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      handleAuthSuccess();
    }
  }, [authenticated, user, redirect_uri, state, getAccessToken]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Processing Authentication...</h2>
        <p>Please wait while we complete your authentication with Privy.</p>
        <div style={{ marginTop: '1rem' }}>
          <div
            style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 2s linear infinite',
              margin: '0 auto',
            }}
          ></div>
        </div>
        <style
          dangerouslySetInnerHTML={{
            __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `,
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <div
          style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '4px',
            margin: '1rem 0',
          }}
        >
          {error}
        </div>
        <div style={{ marginTop: '1rem' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '1rem',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>MCP Server Authentication</h1>

      {redirect_uri && client_id && (
        <div style={{ marginBottom: '2rem' }}>
          <div
            style={{
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              color: '#155724',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.9rem',
              textAlign: 'left',
            }}
          >
            <p>
              <strong>OAuth 2.1 Flow Detected:</strong>{' '}
              {isDebugClient ? '🔧 Debug Mode' : '🚀 Production Mode'}
            </p>
            <p>
              <strong>Client ID:</strong> {client_id}
            </p>
            {response_type && (
              <p>
                <strong>Response Type:</strong> {response_type}
              </p>
            )}
            {scope && (
              <p>
                <strong>Scope:</strong> {scope}
              </p>
            )}
            {state && (
              <p>
                <strong>State:</strong> {state.substring(0, 20)}...
              </p>
            )}
            {code_challenge && (
              <p>
                <strong>PKCE:</strong> Enabled
              </p>
            )}
            {resource && (
              <p>
                <strong>Resource:</strong> {resource}
              </p>
            )}
            <p>
              <strong>Redirect URI:</strong> {redirect_uri}
            </p>
          </div>
        </div>
      )}

      {!authenticated ? (
        <div>
          <p style={{ marginBottom: '2rem', fontSize: '1.1rem' }}>
            Please authenticate with Privy to continue with your MCP server
            session.
          </p>
          <button
            onClick={handleAuth}
            disabled={loading}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
            }}
          >
            {loading ? 'Connecting...' : 'Connect with Privy'}
          </button>
        </div>
      ) : (
        <div>
          <h2 style={{ color: '#28a745' }}>✅ Authentication Successful!</h2>
          <div
            style={{
              backgroundColor: '#d1ecf1',
              border: '1px solid #bee5eb',
              color: '#0c5460',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <p>
              <strong>User ID:</strong> {user?.id}
            </p>
            {user?.wallet?.address && (
              <p>
                <strong>Wallet:</strong> {user.wallet.address}
              </p>
            )}
            {user?.email?.address && (
              <p>
                <strong>Email:</strong> {user.email.address}
              </p>
            )}
          </div>

          {isDebugClient && debugCode ? (
            /* Debug Mode UI */
            <div>
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffecb5',
                  color: '#856404',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                }}
              >
                <h3 style={{ margin: '0 0 1rem 0' }}>🔧 Debug Mode</h3>
                <p>
                  <strong>Authorization Code:</strong>
                </p>
                <div
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    wordBreak: 'break-all',
                    marginBottom: '1rem',
                  }}
                >
                  {debugCode}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    onClick={() => navigator.clipboard.writeText(debugCode)}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '0.5rem',
                    }}
                  >
                    📋 Copy Code
                  </button>
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(debugRedirectUrl || '')
                    }
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    📋 Copy Full URL
                  </button>
                </div>
                <p style={{ fontSize: '0.85rem', margin: 0 }}>
                  <strong>Manual Step:</strong> Copy the authorization code
                  above and paste it into your MCP debugging tool.
                </p>
              </div>
            </div>
          ) : (
            /* Production Mode UI */
            <div>
              <p style={{ fontStyle: 'italic', color: '#6c757d' }}>
                🚀 Automatically redirecting back to your MCP client...
              </p>
              <div
                style={{
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  color: '#155724',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  marginTop: '1rem',
                  fontSize: '0.9rem',
                }}
              >
                <strong>✅ Production Mode:</strong> No manual steps required.
                You'll be redirected automatically.
              </div>
            </div>
          )}

          <button
            onClick={logout}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1rem',
            }}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default AuthComponent;
